<template>
  <div>
    <v-tabs v-model="documentsTab" @update:model-value="onTabChange">
      <v-tab>Contracts</v-tab>
      <v-tab>Invoices</v-tab>
      <v-tab v-if="userHasRole('beta-tester-1')">Quotes</v-tab>
    </v-tabs>

    <v-window v-model="documentsTab">
      <v-window-item>
        <div class="mx-5 mt-5">
          <v-expansion-panels style="border-top: none;">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <template v-slot:default="{ expanded }">
                  <div class="grid grid-cols-4 gap-2">
                    <div class="col-4">
                      Templates
                    </div>
                    <div class="col-8">
                      <v-fade-transition hide-on-leave>
                        <div v-if="!expanded">
                          ({{ validTemplates.length }} {{ $t('templates', validTemplates.length) }})
                        </div>
                      </v-fade-transition>
                    </div>
                  </div>
                </template>

              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-card :loading="documentsLoading.get" variant="flat">
                  <the-help-link url="https://help.fleetwire.io/en/article/what-is-automatic-e-sign-1o5wfxk/"/>
                  <v-alert v-if="!includedInSubscription('esign')" border="bottom" class="card-alert-full" density="compact" type="warning">
                    You plan does not include eSign rental agreements.
                    <v-btn :to="{name: 'settings.account'}" class="float-right" size="x-small">Upgrade</v-btn>
                  </v-alert>

                  <v-card-title>
                    <div class="flex">
                      Document Templates
                      <v-btn size="small" variant="text" @click="learnMoreDialog = true;">
                        <v-icon size="small">mdi-help-circle</v-icon>
                      </v-btn>
                      <v-spacer/>
                      <v-btn :disabled="!planIncludesESign" class="mt-2" size="small" @click="createTemplateInit()">
                        <v-icon class="mr-1" size="small">mdi-plus</v-icon>
                        {{ $t('template') }}
                      </v-btn>
                    </div>
                  </v-card-title>
                  <v-card-text>
                    <TemplatesTableList :templates="documentTemplates"/>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>


        <ContractsTable/>
      </v-window-item>
      <v-window-item v-if="documentsTab === 1">
        <v-card>
          <div class="grid grid-cols-3 gap-2 mx-4 my-10">
            <div>
              <v-card>
                <v-card-title>
                  Number of Invoices
                </v-card-title>
                <v-card-text>
                  {{ documentsMeta.total }}
                </v-card-text>
              </v-card>
            </div>
            <div>
              <v-card>
                <v-card-title>
                  Paid Amount
                </v-card-title>
                <v-card-text>
                  {{ formatMoneyDisplayV2(invoicePaidAmount) }}
                </v-card-text>
              </v-card>
            </div>
            <div>
              <v-card>
                <v-card-title>
                  Turnover
                </v-card-title>
                <v-card-text>
                  {{ formatMoneyDisplayV2(invoiceTotalAmount) }}
                </v-card-text>
              </v-card>
            </div>
          </div>
          <div v-if="orderDocumentsInvoicesFiltered.length === 0" class="text-center border rounded p-4">
            No Invoices found
          </div>
          <v-data-table-server
            v-else
            :disable-sort="true"
            :headers="orderDocumentsHeaders"
            :items="orderDocumentsInvoicesFiltered"
            :items-length="documentsMeta.total"
            :items-per-page="documentsMeta.per_page"
            :items-per-page-options="itemsPerPageOptions"
            :items-per-page-text="'Invoices per page'"
            :mobile-breakpoint="0"
            :options="orderDocumentsTableOptions"
            @update:options="onUpdateOptions"
          >
            <template v-slot:item.title="{ item }">
              <div class="h-100 content-center">
                <router-link :to="{name: 'documents.show', params: { uuid: item.uuid}}">
                  {{ item.title }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.order="{ item }">
              <div class="h-100 content-center">
                <router-link v-if="item?.order?.b_id " :to="{name: 'orders.single', params: { b_id: item?.order?.b_id }}">
                  {{ item?.order?.b_id }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.customer="{ value }">
              <div class="h-100 content-center">
                <router-link v-if="value?.r_id" :to="{name: 'customers.single', params: { r_id: value.r_id }}">
                  {{ value.r_id }}
                </router-link>
              </div>
            </template>
            <template v-slot:item.paid_in_cents="{ item }">
              <div class="h-100 content-center">
                {{ formatMoneyDisplayV2(item.paid_in_cents) }}
              </div>
            </template>
            <template v-slot:item.grand_total_with_tax_in_cents="{ item }">
              <div class="h-100 content-center">
                {{ formatMoneyDisplayV2(item.grand_total_with_tax_in_cents) }}
              </div>
            </template>
            <template v-slot:item.status="{ value }">
              <div class="h-100 content-center">
                {{ value }}
              </div>
            </template>
            <template v-slot:item.date="{ value }">
              <div class="h-100 content-center">
                {{ value }}
              </div>
            </template>
            <template v-slot:item.confirmed="{ value }">
              <div class="h-100 content-center">
                {{ value }}
              </div>
            </template>
          </v-data-table-server>
        </v-card>
      </v-window-item>
      <v-window-item>
        <v-card>
          <div v-if="orderDocuments.length === 0" class="text-center border rounded p-4">
            No contracts found
          </div>
          <v-data-table
            v-else
            :headers="orderDocumentsHeaders"
            :items="orderDocumentsQuotesFiltered"
            :items-per-page="50"
            :mobile-breakpoint="0"
          >
            <template v-slot:item.title="{ item }">
              <router-link :to="{name: 'documents.show', params: { uuid: item.uuid}}">
                {{ item.title }}
              </router-link>
            </template>
            <template v-slot:item.customer="{ item }">
              <span>{{ item.customer }}</span>
            </template>
            <template v-slot:item.grand_total_in_cents="{ item }">
              ${{ formatMoney(item.grand_total_in_cents) }}
            </template>
            <template v-slot:item.grand_total_with_tax_in_cents="{ item }">
              ${{ formatMoney(item.grand_total_with_tax_in_cents) }}
            </template>
          </v-data-table>
        </v-card>
      </v-window-item>
    </v-window>

    <v-dialog v-model="learnMoreDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5">What are document templates?</v-card-title>
        <v-card-text>
          <div>
            Here you can upload a document and insert variable placeholders that will automatically fill in once a
            customer makes a reservation. These are called document templates. You can set up placeholders such as name,
            email, booking dates, vehicle info, signatures, attachments, etc. Once customers book a vehicle, Fleetwire
            can email them a link to their e-sign envelope. We will email you a complete copy for your records upon
            completion.
          </div>
        </v-card-text>
        <v-card-actions>
          <TheHelpLink url="https://help.fleetwire.io/en/article/esign-1o5wfxk/?bust=1643742268392"/>
          <v-spacer/>
          <v-btn @click="learnMoreDialog = false;">
            {{ $t('okay') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </div>
</template>

<script lang="ts" setup>
import {computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useStore} from 'vuex';
import {EventBus} from '@/plugins/eventBus';
import {useMoneyFormatter} from '@/composables/useMoneyFormatter';
import {useAppSettings} from '@/composables/useAppSettings';
import ContractsTable from '@/views/documents/ContractsTable.vue';
import type {DocumentsMeta, OrderDocumentType} from '@/types/order';
import {useSubscription} from '@/composables/useSubscription';
import {useTemplate} from '@/composables/useTemplate';
import {useRoute, useRouter} from 'vue-router';
import type {vTableOptions} from '@/store/modules/booking.ts';


const TemplatesTableList = defineAsyncComponent(() => import('@/components/dashboard/documents/TemplatesTableList.vue'));

const store = useStore();
const {formatMoneyDisplayV2, formatMoney} = useMoneyFormatter();
const {includedInSubscription, planIncludesESign} = useSubscription();
const {fetchTemplates, documentTemplates, documentsLoading} = useTemplate();
const {device, secondaryNavViews, toggleSecondaryImportNav, userHasRole} = useAppSettings();

const documentsTab = ref(0);
const learnMoreDialog = ref(false);

// Hash-based tab navigation
const tabNames = ['contracts', 'invoices', 'quotes'];

const onTabChange = (newTabIndex: unknown) => {
  // Update the URL hash when the tab changes
  const tabIndex = Number(newTabIndex);
  if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < tabNames.length) {
    const tabName = tabNames[tabIndex];
    if (tabName) {
      window.location.hash = tabName;
    }
  }
};

const setTabFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  const tabIndex = tabNames.indexOf(hash);
  if (tabIndex !== -1) {
    documentsTab.value = tabIndex;
  } else if (!hash) {
    // If no hash is set, default to the first tab (contracts)
    documentsTab.value = 0;
    window.location.hash = tabNames[0];
  }
};

const orderDocumentsHeaders = [
  {title: 'Title', sortable: false, key: 'title'},
  {title: 'Booking', key: 'order'},
  {title: 'Customer', key: 'customer'},
  {title: 'Status', key: 'status'},
  {title: 'Date', key: 'date'},
  {title: 'Total w/tax', key: 'grand_total_with_tax_in_cents'},
  {title: 'Paid', key: 'paid_in_cents'},
  {title: 'Confirmed', key: 'confirmed'},
];

const reminderData = ref<{ recipients: any[] }>({recipients: []});

const orderDocuments = computed<OrderDocumentType[]>(() => store.getters['order/documents']);
const documentsMeta = computed<DocumentsMeta>(() => store.getters['order/documentsMeta']);

const orderDocumentsTableOptions = computed({
  get: () => store.getters['order/orderDocumentsTableOptions'],
  set: (value) => store.commit('order/SET_ORDER_DOCUMENTS_TABLE_OPTIONS', value),
});


const itemsPerPageOptions = [
  {value: 15, title: '15'},
  {value: 25, title: '25'},
  {value: 50, title: '50'},
  {value: 100, title: '100'},
  {value: 500, title: '500'},
];

const validTemplates = computed(() => documentTemplates.value.filter(template => template.documents));
const templatesWithoutDocuments = computed(() => documentTemplates.value.filter(template => template.documents === null));

const orderDocumentsQuotesFiltered = computed(() => orderDocuments.value.filter((d) => d.type === 'Document::Quote'));
const orderDocumentsInvoicesFiltered = computed(() => orderDocuments.value.filter((d) => d.type === 'Document::Invoice'));

const invoicePaidAmount = computed(() => {
  const paidInvoices = orderDocumentsInvoicesFiltered.value.filter(invoice => {
    const invoiceStatus = invoice.status?.toLowerCase();
    return invoiceStatus === 'paid' || invoiceStatus === 'overpaid';
  });
  return paidInvoices.reduce((total, invoice) => total + (invoice.paid_in_cents ?? 0), 0);
});

const invoiceTotalAmount = computed(() => {
  return orderDocumentsInvoicesFiltered.value.reduce((total, invoice) => total + (invoice.grand_total_with_tax_in_cents ?? 0), 0);
});

function createTemplateInit() {
  const templatesPending = templatesWithoutDocuments.value;
  if (templatesPending.length === 0) {
    store.dispatch('document/createTemplate');
    return;
  }
  const firstTemplate = templatesPending[0];
  router.push({name: 'documents.prepare', params: {uuid: firstTemplate.uuid}}).catch(() => {
  });
}

const onUpdateOptions = (payload: vTableOptions) => {
  // The first emitting right after mount — ignore it (we already fetched manually)
  if (suppressNextOptionsEvent.value) {
    suppressNextOptionsEvent.value = false
    return
  }

  if (!payload.sortBy[0]) {
    payload.sortBy = [{'key': 'created_at', 'order': 'desc'}];
  }

  triggerFetch(payload)
};

const invoicesBooted = ref(false)
const suppressNextOptionsEvent = ref(false)
const lastKey = ref('')

const normalize = (o: any = {}) => ({
  page: o.page ?? 1,
  itemsPerPage: o.itemsPerPage ?? 25,
  sortBy: (o.sortBy?.length ? o.sortBy : [{key: 'created_at', order: 'desc'}]),
})

const triggerFetch = (opts: any) => {
  const normalized = normalize(opts)
  const key = JSON.stringify({tab: documentsTab.value, ...normalized, type: 'Document::Invoice'})
  if (key === lastKey.value) return
  lastKey.value = key
  store.dispatch('order/getAllOrderDocuments', {...normalized, document_type: 'Document::Invoice'})
}


watch(documentsTab, async (val) => {
  if (val === 1 && !invoicesBooted.value) {
    invoicesBooted.value = true
    await nextTick()
    suppressNextOptionsEvent.value = true   // swallow the table's first emitting
    triggerFetch(orderDocumentsTableOptions.value) // seed fetch
  }
})

const router = useRouter();
const route = useRoute();

watch(() => route.fullPath, () => {
  if (device.value === 'mobile' && secondaryNavViews.value.listingImportNav) {
    toggleSecondaryImportNav();
  }
});

onMounted(() => {
  setTabFromHash(); // Set tab from hash on mount
  if (planIncludesESign.value) {
    fetchTemplates();
  }

  // Listen for hash changes (browser back/forward buttons)
  window.addEventListener('hashchange', setTabFromHash);

  EventBus.$on('create-template-success', (template: any) => {
    if (template.uuid) {
      router.push({name: 'documents.prepare', params: {uuid: template.uuid}}).catch(() => {
      });
    }
  });
});

onBeforeUnmount(() => {
  // Remove hash change listener
  window.removeEventListener('hashchange', setTabFromHash);

  store.commit('envelope/SET_ALL_ENVELOPES', {data: []});
  store.commit('order/SET_ORDER_DOCUMENTS', []);
});
</script>

<style>
.denser .v-btn:not(.v-btn--round).v-size--default {
  height: 21px !important;
}

.denser .v-btn.v-size--default {
  font-size: 10px !important;
}

.denser .v-btn-toggle .v-btn.v-btn.v-size--default {
  min-width: 40px !important;
}
</style>
